"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { FEATURE_FLAG_KEYS } from "@agroproj/backend/convex/constants/featureFlags";
import type { InvestmentProgramCode } from "@agroproj/backend/convex/constants/programs";
import type { ProjectTypeCode, InvestmentResourceSourceCode, TechnicalProjectTypeCode } from "@agroproj/backend/convex/constants/projectTypes";
import type { ProjectBasicFormData } from "@agroproj/backend/convex/validators/project";
import { projectBasicFormSchema } from "@agroproj/backend/convex/validators/project";
import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery } from "convex/react";
import { Building2, ChevronRight, FileText, MapPin, User, XIcon } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { startTransition, useDeferredValue, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useBankOptions } from "@/components/projects/use-bank-options";
import { TableBodyRowEmpty } from "@/components/table-body-row-empty";
import { SelectInput } from "@/components/ui-custom/forms/select-inputs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { fieldErrorClassName, fieldWrapperClassName } from "@/components/ui/form-field-styles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import { RadioGroup } from "@/components/ui/radio-group";
import { Section } from "@/components/ui/section";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";
import { useProjectWizardSearch } from "@/hooks/use-project-wizard-search";
import { EMPTY_VALUE } from "@/lib/format";
import { maskHandler } from "@/lib/mask-handler";
import { captureClientException } from "@/lib/posthog";
import { getUserFacingErrorMessage } from "@/lib/producer-form-behavior";
import { getProjectHarvestOptions } from "@/lib/project-harvest-options";
import { getProjectDetailsDefaultSearch } from "@/lib/router-search-defaults";
import { cn } from "@/lib/utils";
import { ActivitySelectionDialog } from "./activity-selection-dialog";
import { ProjectFormRadioOption } from "./project-form-radio-option";
type ProjectBasicFormValues = {
  selectedProducerId: string;
  technicalProjectType: TechnicalProjectTypeCode | "";
  projectType: ProjectTypeCode | "";
  bankId: string;
  producerClassificationId: string;
  financingFrameworkId: string;
  activityId: string;
  investmentResourceSource: InvestmentResourceSourceCode | "";
  programCode: InvestmentProgramCode | "";
  insuranceModalityId: string;
  harvest: string;
};
const investmentResourceSourceOptions = [{
  value: "bndes" as InvestmentResourceSourceCode,
  label: "BNDES"
}, {
  value: "other_sources" as InvestmentResourceSourceCode,
  label: "Outras fontes"
}] as const;
function validateInvestmentResourceSource(value: ProjectBasicFormValues["investmentResourceSource"]) {
  return value ? undefined : "Fonte de recurso é obrigatória";
}
export function ProjectBasicForm() {
  const harvestOptions = useMemo(() => getProjectHarvestOptions(new Date().getFullYear()), []);
  const params = useParams<{
    organizationSlug: string;
  }>();
  const organizationSlug = params.organizationSlug;
  const router = useRouter();
  const {
    searchParams
  } = useProjectWizardSearch();
  const {
    enabled: investmentCreateFlowEnabled
  } = useConvexFeatureFlag(FEATURE_FLAG_KEYS.INVESTMENT_CREATE_FLOW);
  const newProjectCode = useQuery(api.projects.queries.getNextProjectCode, {});
  const {
    search,
    document,
    page
  } = searchParams;
  const searchTermForQuery = document || search || undefined;
  const producerData = useQuery(api.producers.queries.getProducers, {
    search: searchTermForQuery,
    limit: 25,
    cursor: page !== "1" ? `page-${page}` : undefined
  });
  const stableProducerData = useDeferredValue(producerData);
  const effectiveProducerData = producerData ?? stableProducerData;
  const isLoadingProducers = effectiveProducerData === undefined;
  const producerError = null; // Convex handles errors differently

  const technicalTypes = useQuery(api.base.queries.getProjectTechnicalTypes, {});
  const projectTypes = useQuery(api.base.queries.getProjectTypes, {
    activeOnly: true
  });
  const producerClassifications = useQuery(api.base.queries.getProjectProducerClassifications, {});
  const financingFrameworks = useQuery(api.base.queries.getProjectFinancingFrameworks, {});
  const insuranceModalities = useQuery(api.base.queries.getProjectInsuranceModalities, {});
  const producersData = effectiveProducerData?.producers;
  const paginationInfo = {
    total: producersData?.length ?? 0,
    limit: 25,
    offset: (Number.parseInt(page, 10) - 1) * 25,
    hasMore: producerData?.hasMore === true
  };
  const uniqueLocations = useMemo(() => {
    if (!producersData) {
      return [];
    }
    const locationSet = new Set<string>();
    for (const producer of producersData) {
      const address = producer.primaryAddress;
      if (address?.city && address?.state) {
        locationSet.add(`${address.city} / ${address.state}`);
      }
    }
    return Array.from(locationSet).sort();
  }, [producersData]);

  // Transform producer data to add displayName and get address
  const filteredProducers = useMemo(() => producersData?.filter(producer_0 => {
    if (search && search.length >= 2) {
      const searchTerm = search.toLowerCase();
      const nameMatch = producer_0.name?.toLowerCase().includes(searchTerm) || producer_0.companyName?.toLowerCase().includes(searchTerm) || producer_0.tradingName?.toLowerCase().includes(searchTerm) || producer_0.email?.toLowerCase().includes(searchTerm);
      if (!nameMatch) {
        return false;
      }
    }
    if (document && document.length >= 2) {
      const documentDigits = document.replace(/\D/g, "");
      if (!producer_0.documentValue.includes(documentDigits)) {
        return false;
      }
    }
    return true;
  }).map(producer_1 => {
    const primaryAddress = producer_1.primaryAddress ?? null;
    return {
      ...producer_1,
      displayName: producer_1.documentType === "cpf" ? producer_1.name || EMPTY_VALUE : producer_1.companyName || producer_1.tradingName || EMPTY_VALUE,
      address: primaryAddress ? {
        street: primaryAddress.street ?? undefined,
        number: primaryAddress.number ?? undefined,
        neighborhood: primaryAddress.neighborhood ?? undefined,
        city: primaryAddress.city ?? undefined,
        state: primaryAddress.state ?? undefined
      } : null
    };
  }) ?? [], [producersData, search, document]);
  const createProjectMutation = useMutation(api.projects.mutations.createProject);
  const billingUsage = useQuery(api.payments.queries.getOrganizationBillingUsage);
  const [isProducerDialogOpen, setIsProducerDialogOpen] = useState(false);
  const producerTriggerId = useId();
  const activityTriggerId = useId();
  const technicalTypeLabelId = useId();
  const resourceSourceLabelId = useId();
  const projectTypeLabelId = useId();
  const programLabelId = useId();
  const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
  const defaultValues: ProjectBasicFormValues = {
    selectedProducerId: "",
    technicalProjectType: "",
    projectType: "",
    bankId: "",
    producerClassificationId: "",
    financingFrameworkId: "",
    activityId: "",
    investmentResourceSource: "",
    programCode: "",
    insuranceModalityId: "",
    harvest: ""
  };
  const form = useForm({
    validators: {
      onChange: ({
        value
      }: {
        value: ProjectBasicFormValues;
      }) => {
        try {
          projectBasicFormSchema.parse(value);
          return;
        } catch (error) {
          if (error instanceof Error) {
            return error.message;
          }
          return "Validation error";
        }
      }
    },
    defaultValues,
    onSubmit: async ({
      value: value_0
    }: {
      value: ProjectBasicFormValues;
    }) => {
      try {
        const parsedValue = projectBasicFormSchema.parse(value_0) as ProjectBasicFormData;
        const shouldIncludeInsuranceModality = parsedValue.projectType === "agricultural";
        const isInvestmentProject = parsedValue.technicalProjectType === "investment";
        const projectId = await createProjectMutation({
          producerId: parsedValue.selectedProducerId as Id<"producers">,
          projectCode: newProjectCode ?? "",
          technicalProjectType: parsedValue.technicalProjectType as TechnicalProjectTypeCode,
          projectType: isInvestmentProject ? undefined : parsedValue.projectType as ProjectTypeCode,
          bankId: parsedValue.bankId as Id<"base_banks">,
          producerClassificationId: parsedValue.producerClassificationId as Id<"base_project_producer_classifications">,
          financingFrameworkId: parsedValue.technicalProjectType === "operating_cost" ? parsedValue.financingFrameworkId as Id<"base_project_financing_frameworks"> : undefined,
          activityId: isInvestmentProject ? undefined : parsedValue.activityId as Id<"base_activities">,
          harvest: isInvestmentProject ? undefined : parsedValue.harvest,
          investmentResourceSource: isInvestmentProject ? parsedValue.investmentResourceSource as InvestmentResourceSourceCode : undefined,
          programCode: isInvestmentProject && parsedValue.programCode ? parsedValue.programCode : undefined,
          insuranceModalityId: shouldIncludeInsuranceModality ? parsedValue.insuranceModalityId as Id<"base_project_insurance_modalities"> : undefined
        });
        toast.success("Projeto criado com sucesso");
        const searchParams_0 = new URLSearchParams();
        for (const [key, value_1] of Object.entries(getProjectDetailsDefaultSearch(parsedValue.technicalProjectType))) {
          searchParams_0.set(key, String(value_1));
        }
        startTransition(() => {
          router.push(`/${organizationSlug}/projects/${projectId}?${searchParams_0.toString()}`);
        });
      } catch (error_0) {
        captureClientException(error_0, {
          tags: {
            action: "project.create"
          },
          extra: {
            producerId: value_0.selectedProducerId,
            projectType: value_0.projectType,
            technicalProjectType: value_0.technicalProjectType
          }
        });
        toast.error(getUserFacingErrorMessage(error_0, "Erro ao criar projeto, tente novamente"));
      }
    }
  });
  const projectType = useStore(form.store, state => state.values.projectType);
  const technicalProjectType = useStore(form.store, state_0 => state_0.values.technicalProjectType);
  const investmentResourceSource = useStore(form.store, state_1 => state_1.values.investmentResourceSource);
  const selectedProducerId = useStore(form.store, state_2 => state_2.values.selectedProducerId);
  const selectedBankId = useStore(form.store, state_3 => state_3.values.bankId);
  const selectedFinancingFrameworkId = useStore(form.store, state_4 => state_4.values.financingFrameworkId);
  const selectedActivityId = useStore(form.store, state_5 => state_5.values.activityId);
  const selectedHarvest = useStore(form.store, state_6 => state_6.values.harvest);
  const selectedInsuranceModalityId = useStore(form.store, state_7 => state_7.values.insuranceModalityId);
  const selectedProducerClassificationId = useStore(form.store, state_8 => state_8.values.producerClassificationId);
  const selectedProgramCode = useStore(form.store, state_9 => state_9.values.programCode);
  const isInvestmentProject_0 = technicalProjectType === "investment";
  const investmentPrograms = useQuery(api.base.queries.getInvestmentPrograms, isInvestmentProject_0 && investmentResourceSource && selectedProducerClassificationId ? {
    resourceSource: investmentResourceSource,
    producerClassificationId: selectedProducerClassificationId as Id<"base_project_producer_classifications">
  } : "skip");
  const bankOptions = useBankOptions(selectedBankId);
  const isSubmitting = useStore(form.store, state_10 => state_10.isSubmitting);
  const selectedProducer = useMemo(() => filteredProducers.find(producer_2 => producer_2._id === selectedProducerId), [filteredProducers, selectedProducerId]);
  const shouldShowInsuranceModality = !isInvestmentProject_0 && projectType === "agricultural";
  const previousInvestmentResourceSource = useRef(investmentResourceSource);
  const investmentProgramRequired = isInvestmentProject_0 && investmentResourceSource && selectedProducerClassificationId && investmentPrograms !== undefined && investmentPrograms.length > 0;
  const sharedRequiredFieldsReady = Boolean(selectedProducerId && selectedBankId && selectedProducerClassificationId && technicalProjectType);
  const operatingCostRequiredFieldsReady = Boolean(projectType && selectedActivityId && selectedFinancingFrameworkId && selectedHarvest && (!shouldShowInsuranceModality || selectedInsuranceModalityId));
  const investmentRequiredFieldsReady = Boolean(investmentResourceSource) && investmentPrograms !== undefined && (!investmentProgramRequired || Boolean(selectedProgramCode));
  const projectLimitReached = billingUsage?.canCreateProject === false;
  const projectLimit = billingUsage?.projectLimit ?? null;
  const billableProjectCount = billingUsage?.billableProjectCount ?? 0;
  const canSubmitProject = !isSubmitting && !projectLimitReached && sharedRequiredFieldsReady && (isInvestmentProject_0 ? investmentRequiredFieldsReady : operatingCostRequiredFieldsReady);
  useEffect(() => {
    if (!shouldShowInsuranceModality && selectedInsuranceModalityId) {
      form.setFieldValue("insuranceModalityId", "");
    }
  }, [form, selectedInsuranceModalityId, shouldShowInsuranceModality]);
  useEffect(() => {
    if (technicalProjectType === "investment") {
      form.setFieldValue("projectType", "");
      form.setFieldValue("activityId", "");
      form.setFieldValue("insuranceModalityId", "");
      form.setFieldValue("harvest", "");
      return;
    }
    if (technicalProjectType === "operating_cost") {
      form.setFieldValue("investmentResourceSource", "");
      form.setFieldValue("programCode", "");
    }
  }, [form, technicalProjectType]);
  useEffect(() => {
    if (isInvestmentProject_0 && previousInvestmentResourceSource.current !== investmentResourceSource) {
      form.setFieldValue("producerClassificationId", "");
      form.setFieldValue("programCode", "");
    }
    previousInvestmentResourceSource.current = investmentResourceSource;
  }, [form, investmentResourceSource, isInvestmentProject_0]);

  // Programa cascades off Fonte de Recurso + Enquadramento. Whenever the available set no
  // longer contains the current selection (upstream change, project-type switch, or an
  // empty cascade such as PRONAF), clear it so a stale program is never submitted. If the
  // cascade resolves to a single option, select it automatically to match the product flow.
  useEffect(() => {
    if (!investmentPrograms) {
      return;
    }
    if (investmentPrograms.length === 1 && selectedProgramCode !== investmentPrograms[0]?.code) {
      form.setFieldValue("programCode", investmentPrograms[0]?.code ?? "");
      return;
    }
    if (selectedProgramCode && !investmentPrograms.some(p => p.code === selectedProgramCode)) {
      form.setFieldValue("programCode", "");
    }
  }, [form, investmentPrograms, selectedProgramCode]);
  return <form onSubmit={e => {
    e.preventDefault();
    setHasSubmitAttempt(true);
    void form.handleSubmit();
  }}>
      <div className="space-y-4">
        {projectLimitReached && projectLimit !== null ? <Alert variant="destructive">
            <AlertTitle>Limite de projetos do plano</AlertTitle>
            <AlertDescription>
              Seu plano atual permite até {projectLimit} projeto
              {projectLimit === 1 ? "" : "s"} ({billableProjectCount} em uso). Cancele um projeto
              existente ou{" "}
              <Link className="text-foreground font-medium underline underline-offset-4" href={`/${organizationSlug}/settings/billing` as Route}>
                ver planos
              </Link>{" "}
              para criar outro.
            </AlertDescription>
          </Alert> : null}
        <Section icon={FileText} title="Dados do projeto">
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input disabled label="Código do projeto" value={newProjectCode ?? ""} />
              <div className={fieldWrapperClassName}>
                <Label htmlFor={producerTriggerId}>Produtor</Label>
                <form.Field name="selectedProducerId">
                  {field => <Dialog open={isProducerDialogOpen} onOpenChange={setIsProducerDialogOpen}>
                      {(() => {
                    const handleProducerSelect = (producerId: string) => {
                      field.handleChange(producerId);
                      requestAnimationFrame(() => {
                        setIsProducerDialogOpen(false);
                      });
                    };
                    return <>
                            <DialogTrigger render={<Button className={cn("w-full justify-between font-normal", !selectedProducer && "text-muted-foreground")} id={producerTriggerId} variant="outline" />}>
                              <span className="truncate">
                                {selectedProducer?.displayName || "Selecionar produtor"}
                              </span>
                              <ChevronRight className="size-4 opacity-50" />
                            </DialogTrigger>
                            <DialogContent className="flex max-h-[85vh] w-full max-w-[90vw] flex-col sm:max-w-6xl">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <User className="size-5" />
                                  Seleção do produtor
                                </DialogTitle>
                                <DialogDescription>
                                  Escolha o produtor para associar ao projeto.
                                </DialogDescription>
                              </DialogHeader>

                              <div className="flex min-h-0 flex-1 flex-col">
                                <div className="mb-6">
                                  <ProducerSelectionFilters uniqueLocations={uniqueLocations} />
                                </div>

                                {producerError && <Alert className="mb-6" variant="destructive">
                                    <AlertDescription>
                                      Erro ao carregar produtores: {String(producerError)}
                                    </AlertDescription>
                                  </Alert>}

                                <TableContainer className="min-h-0 flex-1 overflow-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="w-8">
                                          <span className="sr-only">Selecionar</span>
                                        </TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Nome</TableHead>
                                        <TableHead>Localização</TableHead>
                                        <TableHead>Status</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {isLoadingProducers && <TableBodyRowEmpty columns={5} message="Carregando..." />}

                                      {!(isLoadingProducers || filteredProducers.length) && <TableBodyRowEmpty columns={5} message="Nenhum produtor encontrado com os filtros aplicados" />}

                                      {filteredProducers.map(producer_3 => <TableRow className="cursor-pointer" data-state={field.state.value === producer_3._id ? "selected" : undefined} key={producer_3._id} onClick={() => {
                                  handleProducerSelect(producer_3._id);
                                }}>
                                          <TableCell>
                                            <input aria-label={`Selecionar ${producer_3.displayName}`} checked={producer_3._id === field.state.value} className="size-4" name="producer-selection" onChange={() => {
                                      handleProducerSelect(producer_3._id);
                                    }} type="radio" />
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-2">
                                              {producer_3.documentType === "cpf" ? <>
                                                  <User className="text-muted-foreground size-4" />
                                                  <span>Pessoa física</span>
                                                </> : <>
                                                  <Building2 className="text-muted-foreground size-4" />
                                                  <span>Pessoa jurídica</span>
                                                </>}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div>
                                              <div className="font-medium">
                                                {producer_3.displayName}
                                              </div>
                                              <div className="text-muted-foreground font-mono">
                                                {maskHandler(producer_3.documentType.toUpperCase() as "CPF" | "CNPJ", producer_3.documentValue)}
                                              </div>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1">
                                              <MapPin className="text-muted-foreground size-3" />
                                              {producer_3.address ? (() => {
                                        const locationParts = [producer_3.address.street && producer_3.address.number ? `${producer_3.address.street}, ${producer_3.address.number}` : producer_3.address.street, producer_3.address.neighborhood, producer_3.address.city && producer_3.address.state ? `${producer_3.address.city} / ${producer_3.address.state}` : undefined];
                                        const location = locationParts.filter(value_2 => value_2?.trim()).join(" • ");
                                        return <span>{location || EMPTY_VALUE}</span>;
                                      })() : EMPTY_VALUE}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <Badge variant={producer_3.status === "active" ? "status-active" : "status-inactive"}>
                                              {producer_3.status === "active" ? "Ativo" : "Inativo"}
                                            </Badge>
                                          </TableCell>
                                        </TableRow>)}
                                    </TableBody>
                                  </Table>
                                </TableContainer>

                                <ProducerSelectionPagination paginationInfo={paginationInfo} />
                              </div>
                            </DialogContent>
                          </>;
                  })()}
                    </Dialog>}
                </form.Field>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Technical Project Type */}
              <form.Field name="technicalProjectType">
                {field_0 => <div className={fieldWrapperClassName}>
                    <Label id={technicalTypeLabelId}>Tipo de projeto técnico</Label>
                    <RadioGroup<ProjectBasicFormValues["technicalProjectType"]> aria-labelledby={technicalTypeLabelId} className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4" onValueChange={field_0.handleChange} value={field_0.state.value}>
                      {technicalTypes?.map(type => {
                    const isActive = type.status === "active";
                    const isInvestmentType = type._id === "investment";
                    const isDisabled = !isActive || isInvestmentType && !investmentCreateFlowEnabled;
                    return <ProjectFormRadioOption disabled={isDisabled} id={type._id} key={type._id} label={type.title} value={type._id} />;
                  })}
                    </RadioGroup>
                    {field_0.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                        {field_0.state.meta.errors[0] ?? "Erro de validação"}
                      </p>}
                  </div>}
              </form.Field>

              {/* Financial institution (bank) selection */}
              <form.Field name="bankId">
                {field_1 => <div className={fieldWrapperClassName}>
                    <SelectInput label={isInvestmentProject_0 ? "Cooperativa" : "Instituição financeira"} onValueChange={value_3 => field_1.handleChange(value_3)} options={bankOptions} placeholder={isInvestmentProject_0 ? "Selecione uma cooperativa" : "Selecione uma instituição financeira"} value={field_1.state.value} />
                    {field_1.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                        {field_1.state.meta.errors[0] ?? "Erro de validação"}
                      </p>}
                  </div>}
              </form.Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {isInvestmentProject_0 ? <form.Field name="investmentResourceSource" validators={{
              onChange: ({
                value: value_4
              }) => validateInvestmentResourceSource(value_4),
              onSubmit: ({
                value: value_5
              }) => validateInvestmentResourceSource(value_5)
            }}>
                  {field_2 => <div className={fieldWrapperClassName}>
                      <Label id={resourceSourceLabelId}>Fonte de recurso</Label>
                      <RadioGroup<ProjectBasicFormValues["investmentResourceSource"]> aria-labelledby={resourceSourceLabelId} className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4" onValueChange={field_2.handleChange} value={field_2.state.value}>
                        {investmentResourceSourceOptions.map(option => <ProjectFormRadioOption id={option.value} key={option.value} label={option.label} value={option.value} />)}
                      </RadioGroup>
                      {(field_2.state.meta.errors.length > 0 || hasSubmitAttempt && !investmentResourceSource) && <p className={fieldErrorClassName}>
                          {field_2.state.meta.errors[0] ?? "Fonte de recurso é obrigatória"}
                        </p>}
                    </div>}
                </form.Field> : <form.Field name="projectType">
                  {field_3 => <div className={fieldWrapperClassName}>
                      <Label id={projectTypeLabelId}>Tipo do projeto</Label>
                      <RadioGroup<ProjectBasicFormValues["projectType"]> aria-labelledby={projectTypeLabelId} className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4" onValueChange={value_6 => {
                  field_3.handleChange(value_6);
                  form.setFieldValue("activityId", "");
                }} value={field_3.state.value}>
                        {projectTypes?.map(type_0 => <ProjectFormRadioOption id={type_0._id} key={type_0._id} label={type_0.title} value={type_0._id} />)}
                      </RadioGroup>
                      {field_3.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                          {field_3.state.meta.errors[0] ?? "Erro de validação"}
                        </p>}
                    </div>}
                </form.Field>}

              {!isInvestmentProject_0 && <form.Field name="financingFrameworkId">
                  {field_4 => <SelectInput label="Enquadramento financeiro" disabled={financingFrameworks === undefined} onValueChange={field_4.handleChange} options={financingFrameworks?.map(item => ({
                value: item._id,
                label: item.title
              })) ?? []} placeholder="Selecione o enquadramento financeiro" value={field_4.state.value} />}
                </form.Field>}

              {isInvestmentProject_0 && !investmentResourceSource ? null : <form.Field name="producerClassificationId">
                  {field_5 => <div className={fieldWrapperClassName}>
                      <SelectInput label="Enquadramento do produtor" onValueChange={value_7 => field_5.handleChange(value_7)} options={producerClassifications?.map(classification => ({
                  value: classification._id,
                  label: classification.title
                })) ?? []} placeholder="Selecione o enquadramento" value={field_5.state.value} />
                      {field_5.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                          {field_5.state.meta.errors[0] ?? "Erro de validação"}
                        </p>}
                    </div>}
                </form.Field>}
            </div>

            {isInvestmentProject_0 && investmentResourceSource && selectedProducerClassificationId ? <form.Field name="programCode" validators={{
            onSubmit: ({
              value: value_8
            }) => investmentPrograms && investmentPrograms.length > 0 && !value_8 ? "Programa é obrigatório" : undefined
          }}>
                {field_6 => <div className={fieldWrapperClassName}>
                    <Label id={programLabelId}>Programa</Label>
                    {investmentPrograms === undefined ? <output className="text-muted-foreground">Carregando programas...</output> : investmentPrograms.length === 0 ? <EmptyState size="sm" title="Nenhum programa disponível para a fonte de recurso e enquadramento selecionados" /> : <RadioGroup<ProjectBasicFormValues["programCode"]> aria-labelledby={programLabelId} className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:grid-cols-3" onValueChange={field_6.handleChange} value={field_6.state.value}>
                        {investmentPrograms.map(option_0 => <ProjectFormRadioOption id={`program-${option_0.code}`} key={option_0.code} label={option_0.title} value={option_0.code} />)}
                      </RadioGroup>}
                    {(field_6.state.meta.errors.length > 0 || hasSubmitAttempt && investmentPrograms && investmentPrograms.length > 0 && !selectedProgramCode) && <p className={fieldErrorClassName}>
                        {field_6.state.meta.errors[0] ?? "Programa é obrigatório"}
                      </p>}
                  </div>}
              </form.Field> : null}

            {!isInvestmentProject_0 ? <form.Field name="activityId">
                {field_7 => <div className={fieldWrapperClassName}>
                    <Label htmlFor={activityTriggerId}>Cultura/atividade</Label>
                    <ActivitySelectionDialog triggerId={activityTriggerId} projectType={projectType ? projectType as ProjectTypeCode : undefined} value={field_7.state.value as Id<"base_activities">} onChange={value_9 => field_7.handleChange(value_9)} disabled={!projectType} />
                    {field_7.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                        {field_7.state.meta.errors[0] ?? "Erro de validação"}
                      </p>}
                  </div>}
              </form.Field> : null}

            {!isInvestmentProject_0 ? <div className={cn("grid grid-cols-1 gap-4", shouldShowInsuranceModality && "sm:grid-cols-2")}>
                {shouldShowInsuranceModality ? <form.Field name="insuranceModalityId" validators={{
              onChange: ({
                value: value_10
              }) => !value_10 ? "A modalidade do seguro é obrigatória" : undefined
            }}>
                    {field_8 => <div className={fieldWrapperClassName}>
                        <SelectInput label="Modalidade do seguro" onValueChange={value_11 => field_8.handleChange(value_11)} options={insuranceModalities?.map(insurance => ({
                  value: insurance._id,
                  label: insurance.title
                })) ?? []} placeholder="Selecione o seguro" value={field_8.state.value ?? ""} />
                        {field_8.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                            {field_8.state.meta.errors[0] ?? "Erro de validação"}
                          </p>}
                      </div>}
                  </form.Field> : null}

                {/* Harvest */}
                <form.Field name="harvest">
                  {field_9 => <div className={fieldWrapperClassName}>
                      <SelectInput label="Safra" onValueChange={value_12 => field_9.handleChange(value_12)} options={harvestOptions.map(harvest => ({
                  value: harvest.value,
                  label: harvest.label
                }))} placeholder="Selecione a safra" value={field_9.state.value ?? ""} />
                      {field_9.state.meta.errors.length > 0 && <p className={fieldErrorClassName}>
                          {field_9.state.meta.errors[0] ?? "Erro de validação"}
                        </p>}
                    </div>}
                </form.Field>
              </div> : null}
          </div>
        </Section>

        <div className="flex justify-end">
          <Button size="sm" className="min-w-32" disabled={!canSubmitProject} type="submit">
            {isSubmitting ? "Criando..." : "Criar projeto"}
          </Button>
        </div>
      </div>
    </form>;
}
function ProducerSelectionPagination(t0) {
  const $ = _c(16);
  const {
    paginationInfo
  } = t0;
  const {
    searchParams,
    updateSearch
  } = useProjectWizardSearch();
  let t1;
  if ($[0] !== searchParams.page) {
    t1 = Number.parseInt(searchParams.page || "1", 10);
    $[0] = searchParams.page;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentPage = t1;
  const totalPages = Math.ceil(paginationInfo.total / paginationInfo.limit);
  const startItem = paginationInfo.offset + 1;
  const endItem = Math.min(paginationInfo.offset + paginationInfo.limit, paginationInfo.total);
  const canGoNext = paginationInfo.hasMore;
  let t2;
  if ($[2] !== updateSearch) {
    t2 = newPage => {
      updateSearch({
        page: newPage.toString()
      });
    };
    $[2] = updateSearch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handlePageChange = t2;
  if (paginationInfo.total === 0) {
    return null;
  }
  let t3;
  if ($[4] !== endItem || $[5] !== paginationInfo.total || $[6] !== startItem) {
    t3 = <div className="text-muted-foreground flex-1 tabular-nums">Mostrando {startItem} a {endItem} de {paginationInfo.total} produtores</div>;
    $[4] = endItem;
    $[5] = paginationInfo.total;
    $[6] = startItem;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== canGoNext || $[9] !== currentPage || $[10] !== handlePageChange || $[11] !== totalPages) {
    t4 = <Pagination currentPage={currentPage} hasNextPage={canGoNext} onPageChange={handlePageChange} showFirstLast={true} totalPages={totalPages} />;
    $[8] = canGoNext;
    $[9] = currentPage;
    $[10] = handlePageChange;
    $[11] = totalPages;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex items-center justify-between px-2 py-4">{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function ProducerSelectionFilters(t0) {
  const $ = _c(33);
  const {
    searchParams,
    updateSearch
  } = useProjectWizardSearch();
  const {
    search,
    document,
    location
  } = searchParams;
  const searchDebounceRef = useRef(null);
  const documentDebounceRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (documentDebounceRef.current) {
        clearTimeout(documentDebounceRef.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      if (documentDebounceRef.current) {
        clearTimeout(documentDebounceRef.current);
        documentDebounceRef.current = null;
      }
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== document || $[4] !== search) {
    t4 = [search, document];
    $[3] = document;
    $[4] = search;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== search || $[7] !== updateSearch) {
    t5 = nextValue => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (!nextValue && search) {
        updateSearch({
          search: "",
          page: "1"
        });
        return;
      }
      searchDebounceRef.current = setTimeout(() => {
        updateSearch({
          search: nextValue,
          page: "1"
        });
        searchDebounceRef.current = null;
      }, 300);
    };
    $[6] = search;
    $[7] = updateSearch;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleSearchChange = t5;
  let t6;
  if ($[9] !== document || $[10] !== updateSearch) {
    t6 = nextValue_0 => {
      if (documentDebounceRef.current) {
        clearTimeout(documentDebounceRef.current);
      }
      if (!nextValue_0 && document) {
        updateSearch({
          document: "",
          page: "1"
        });
        return;
      }
      documentDebounceRef.current = setTimeout(() => {
        updateSearch({
          document: nextValue_0,
          page: "1"
        });
        documentDebounceRef.current = null;
      }, 300);
    };
    $[9] = document;
    $[10] = updateSearch;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleDocumentChange = t6;
  let t7;
  if ($[12] !== handleSearchChange) {
    t7 = e => handleSearchChange(e.target.value);
    $[12] = handleSearchChange;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== search || $[15] !== t7) {
    t8 = <div className="min-w-52 flex-1"><Input className="w-full" key={search} onChange={t7} placeholder={"Buscar por nome ou raz\xE3o social..."} defaultValue={search} /></div>;
    $[14] = search;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== handleDocumentChange) {
    t9 = e_0 => handleDocumentChange(e_0.target.value);
    $[17] = handleDocumentChange;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== document || $[20] !== t9) {
    t10 = <div className="min-w-64 flex-[1.2]"><Input className="w-full" key={document} onChange={t9} placeholder="Buscar por CPF/CNPJ..." defaultValue={document} /></div>;
    $[19] = document;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div className="mb-4 flex flex-wrap gap-4">{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== document || $[26] !== location || $[27] !== search || $[28] !== updateSearch) {
    t12 = (search || document || location.length > 0) && <div className="mb-4 flex flex-wrap gap-2">{search && <Badge variant="secondary">Nome: {search}<FilterRemoveButton label={`Nome: ${search}`} onRemove={() => updateSearch({
          search: "",
          page: "1"
        })} /></Badge>}{document && <Badge variant="secondary">Documento: {document}<FilterRemoveButton label={`Documento: ${document}`} onRemove={() => updateSearch({
          document: "",
          page: "1"
        })} /></Badge>}{location.map(item => <Badge key={item} variant="secondary">Localização: {item}<FilterRemoveButton label={`Localização: ${item}`} onRemove={() => updateSearch({
          location: location.filter(l => l !== item),
          page: "1"
        })} /></Badge>)}</div>;
    $[25] = document;
    $[26] = location;
    $[27] = search;
    $[28] = updateSearch;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}
function FilterRemoveButton(t0) {
  const $ = _c(4);
  const {
    label,
    onRemove
  } = t0;
  const t1 = `Remover filtro: ${label}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <XIcon aria-hidden="true" className="size-3" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== onRemove || $[2] !== t1) {
    t3 = <button aria-label={t1} className="hover:bg-muted ml-0.5 rounded-full" onClick={onRemove} type="button">{t2}</button>;
    $[1] = onRemove;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}